import { useCallback, useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useT } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { MediaField } from "@/pages/coach/methods/MediaField";
import { KIND_META, STEP_KINDS, STEP_ICON, VISIBILITIES, kindIcon } from "@/pages/coach/methods/shared";
import { Sparkles, Plus, Trash2, ChevronUp, ChevronDown, Lock, Users, Globe, Bookmark, ShieldCheck, Loader2, Leaf } from "lucide-react";

const AUDIO_KINDS = ["audio", "body_scan", "gratitude", "visualization"];
const VIDEO_KINDS = ["video", "movement"];
const QUESTION_KINDS = ["questions", "check_in", "gratitude"];
const VIS_ICON = { private: Lock, shared_with_clients: Users, public: Globe };
const STEP_DEFAULTS = {
  breathwork: { duration_s: 120 }, body_scan: { body: "", duration_s: 180 }, gratitude: { body: "" },
  audio: { duration_s: 60 }, video: {}, text: { body: "" }, timer: { duration_s: 60 },
  visualization: { body: "" }, movement: { body: "" }, questions: { questions: [] }, check_in: { questions: [] },
};
const emptyForm = {
  title: "", subtitle: "", description: "", cover_image_url: "", audio_url: "", video_url: "",
  duration_min: "", preview_seconds: 15, visibility: "private", is_template: false, price_credits: "",
  questions: [], steps: [], breathwork_technique_id: null,
};

export function MethodEditorDialog({ open, methodId, initialKind, onClose }) {
  const t = useT();
  const [stage, setStage] = useState("pick");
  const [kind, setKind] = useState("audio");
  const [form, setForm] = useState(emptyForm);
  const [library, setLibrary] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [suggestions, setSuggestions] = useState([]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  useEffect(() => {
    if (!open) return;
    api.get("/coach/breathwork-techniques").then(({ data }) => setLibrary(Array.isArray(data) ? data : (data?.techniques || []))).catch(() => setLibrary([]));
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setSuggestions([]);
    if (methodId) {
      setLoading(true);
      api.get(`/coach/methods/${methodId}`).then(({ data: m }) => {
        setKind(m.kind || "audio");
        setForm({
          title: m.title || "", subtitle: m.subtitle || "", description: m.description || "",
          cover_image_url: m.cover_image_url || "", audio_url: m.audio_url || "", video_url: m.video_url || "",
          duration_min: m.duration_min || "", preview_seconds: m.preview_seconds ?? 15,
          visibility: m.visibility || "private", is_template: !!m.is_template, price_credits: m.price_credits || "",
          questions: Array.isArray(m.questions) ? m.questions : [], steps: Array.isArray(m.steps) ? m.steps : [],
          breathwork_technique_id: m.breathwork_technique_id || null,
        });
        setStage("edit");
      }).catch(() => toast.error(t("cm.load_failed"))).finally(() => setLoading(false));
    } else if (initialKind) {
      setKind(initialKind); setForm(emptyForm); setStage("edit");
    } else {
      setForm(emptyForm); setStage("pick");
    }
  }, [open, methodId, initialKind, t]);

  const suggestTitles = async () => {
    if (form.description.trim().length < 8) return;
    setSuggesting(true); setSuggestions([]);
    try {
      const { data } = await api.post("/coach/methods/suggest-titles", { description: form.description.trim(), kind });
      const titles = data?.titles || [];
      setSuggestions(titles);
      if (titles.length === 0) toast.info(t("cm.ai_no_titles"));
    } catch (err) {
      const status = err?.response?.status;
      toast.error(status === 402 || status === 403 ? t("cm.ai_locked") : apiError(err));
    } finally { setSuggesting(false); }
  };

  const save = async () => {
    if (form.title.trim().length < 1) { toast.error(t("cm.title_required")); return; }
    setSaving(true);
    const body = {
      kind,
      title: form.title.trim(),
      subtitle: form.subtitle.trim() || null,
      description: form.description.trim() || null,
      cover_image_url: form.cover_image_url.trim() || null,
      audio_url: form.audio_url.trim() || null,
      video_url: form.video_url.trim() || null,
      duration_min: form.duration_min ? parseInt(form.duration_min) : null,
      preview_seconds: parseInt(form.preview_seconds) || 15,
      breathwork_technique_id: kind === "breathwork" ? form.breathwork_technique_id : null,
      questions: QUESTION_KINDS.includes(kind) ? form.questions.filter((q) => q.prompt?.trim()) : null,
      steps: kind === "combo" ? form.steps : null,
      is_template: form.is_template,
      visibility: form.visibility,
      price_credits: form.price_credits ? parseInt(form.price_credits) : 0,
    };
    try {
      if (methodId) await api.patch(`/coach/methods/${methodId}`, body);
      else await api.post("/coach/methods", body);
      toast.success(t("cm.saved"));
      onClose(true);
    } catch (err) { toast.error(apiError(err, t("cm.save_failed"))); }
    finally { setSaving(false); }
  };

  const meta = KIND_META.find((m) => m.key === kind) || KIND_META[0];
  const KindIcon = kindIcon(kind);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose(false)}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto" data-testid="cm-editor-dialog">
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>
        ) : stage === "pick" ? (
          <>
            <DialogHeader>
              <DialogTitle>{t("cm.pick_title")}</DialogTitle>
              <DialogDescription>{t("cm.pick_hint")}</DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              {KIND_META.map((m) => {
                const Icon = m.icon;
                return (
                  <button key={m.key} onClick={() => { setKind(m.key); setStage("edit"); }} data-testid={`cm-kind-${m.key}`} className="flex flex-col items-start gap-2 rounded-2xl border border-border bg-card p-3 text-left transition-colors hover:border-primary hover:bg-accent">
                    <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/15"><Icon className="h-5 w-5 text-primary" /></div>
                    <p className="text-sm font-semibold text-foreground">{t(`cm.kind_${m.key}`)}</p>
                    <p className="text-[11px] leading-snug text-muted-foreground">{t(`cm.kind_${m.key}_sub`)}</p>
                  </button>
                );
              })}
            </div>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><KindIcon className="h-5 w-5 text-primary" /> {methodId ? t("cm.editing") : t(`cm.kind_${kind}`)}</DialogTitle>
              <DialogDescription>{t(`cm.kind_${kind}_sub`)}</DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              {/* Cover */}
              <Field label={t("cm.cover")}>
                <MediaField kind="photo" value={form.cover_image_url} onChange={(v) => set({ cover_image_url: v })} testID="cm-cover" />
              </Field>

              {/* Title + AI */}
              <div>
                <div className="mb-1.5 flex items-center justify-between">
                  <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("cm.f_title")}</label>
                  <Button type="button" size="sm" variant="outline" className="h-7 gap-1 rounded-full text-xs" disabled={form.description.trim().length < 8 || suggesting} onClick={suggestTitles} data-testid="cm-suggest-titles">
                    {suggesting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />} {t("cm.suggest_titles")}
                  </Button>
                </div>
                <Input value={form.title} onChange={(e) => set({ title: e.target.value })} maxLength={140} placeholder={t("cm.title_ph")} data-testid="cm-title" />
                {suggestions.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5 rounded-xl border border-primary/30 bg-primary/5 p-2" data-testid="cm-suggest-box">
                    {suggestions.map((sg, i) => (
                      <button key={i} onClick={() => { set({ title: sg }); setSuggestions([]); }} data-testid={`cm-suggest-${i}`} className="inline-flex items-center gap-1 rounded-full border border-border bg-card px-2.5 py-1 text-xs text-foreground hover:border-primary">
                        <Sparkles className="h-3 w-3 text-primary" /> {sg}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <Field label={t("cm.f_subtitle")}>
                <Input value={form.subtitle} onChange={(e) => set({ subtitle: e.target.value })} maxLength={280} placeholder={t("cm.subtitle_ph")} data-testid="cm-subtitle" />
              </Field>
              <Field label={t("cm.description")}>
                <Textarea className="min-h-[90px]" value={form.description} onChange={(e) => set({ description: e.target.value })} maxLength={2000} placeholder={t("cm.description_ph")} data-testid="cm-description" />
              </Field>

              {/* Type-specific */}
              {AUDIO_KINDS.includes(kind) && (
                <Field label={t("cm.audio_field")}><MediaField kind="audio" value={form.audio_url} onChange={(v) => set({ audio_url: v })} testID="cm-audio" /></Field>
              )}
              {VIDEO_KINDS.includes(kind) && (
                <Field label={t("cm.video_field")}><MediaField kind="video" value={form.video_url} onChange={(v) => set({ video_url: v })} testID="cm-video" /></Field>
              )}
              {kind === "breathwork" && (
                <Field label={t("cm.pick_technique")}>
                  <TechniquePicker library={library} value={form.breathwork_technique_id} onChange={(id) => set({ breathwork_technique_id: id })} />
                </Field>
              )}
              {QUESTION_KINDS.includes(kind) && (
                <QuestionsEditor questions={form.questions} onChange={(q) => set({ questions: q })} />
              )}
              {kind === "combo" && (
                <StepsEditor steps={form.steps} onChange={(s) => set({ steps: s })} library={library} />
              )}

              {/* Settings */}
              <p className="pt-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("cm.settings")}</p>
              <div className="grid grid-cols-2 gap-3">
                <Field label={t("cm.duration_min")}>
                  <Input type="number" min="1" max="240" value={form.duration_min} onChange={(e) => set({ duration_min: e.target.value })} placeholder="5" data-testid="cm-duration-min" />
                </Field>
                <Field label={t("cm.preview_seconds")}>
                  <Input type="number" min="0" max="120" value={form.preview_seconds} onChange={(e) => set({ preview_seconds: e.target.value })} placeholder="15" data-testid="cm-preview-seconds" />
                </Field>
              </div>

              <Field label={t("cm.visibility")}>
                <div className="flex gap-1.5">
                  {VISIBILITIES.map((v) => {
                    const Icon = VIS_ICON[v];
                    return (
                      <button key={v} onClick={() => set({ visibility: v })} data-testid={`cm-vis-${v}`} className={`flex flex-1 items-center justify-center gap-1.5 rounded-xl border py-2 text-sm font-medium ${form.visibility === v ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground hover:bg-accent"}`}>
                        <Icon className="h-4 w-4" /> {t(`cm.vis_${v}`)}
                      </button>
                    );
                  })}
                </div>
              </Field>

              <div className="grid grid-cols-2 gap-3">
                <Field label={t("cm.price")}>
                  <Input type="number" min="0" value={form.price_credits} onChange={(e) => set({ price_credits: e.target.value })} placeholder="0" data-testid="cm-price" />
                </Field>
                <Field label={t("cm.is_template")}>
                  <button onClick={() => set({ is_template: !form.is_template })} data-testid="cm-template-toggle" className={`flex h-10 w-full items-center justify-center gap-1.5 rounded-xl border text-sm font-medium ${form.is_template ? "border-primary bg-primary text-primary-foreground" : "border-border text-primary hover:bg-accent"}`}>
                    <Bookmark className="h-4 w-4" /> {form.is_template ? t("cm.tpl_on") : t("cm.tpl_off")}
                  </button>
                </Field>
              </div>

              <div className="flex items-start gap-2 rounded-xl border border-primary/30 bg-primary/5 p-3" data-testid="cm-ethics">
                <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                <div>
                  <p className="text-xs font-semibold text-primary">{t("cm.ethics_title")}</p>
                  <p className="mt-0.5 text-[11px] leading-snug text-muted-foreground">{t("cm.ethics_body")}</p>
                </div>
              </div>

              <div className="flex gap-2 pt-1">
                {!methodId && <Button variant="outline" className="rounded-full" onClick={() => setStage("pick")}>{t("cm.change_kind")}</Button>}
                <Button className="flex-1 rounded-full" onClick={save} disabled={saving || !form.title.trim()} data-testid="cm-save">{saving ? t("common.saving") : methodId ? t("common.save") : t("cm.save_new")}</Button>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</label>
      {children}
    </div>
  );
}

function TechniquePicker({ library, value, onChange }) {
  const t = useT();
  if (!library.length) {
    return (
      <div className="flex items-center gap-2 rounded-xl border border-dashed border-border bg-card p-3 text-sm text-muted-foreground">
        <Leaf className="h-4 w-4 text-primary" /> {t("cm.no_bw_yet")}
      </div>
    );
  }
  return (
    <div className="space-y-1.5">
      {library.map((b) => {
        const active = value === b.id;
        return (
          <button key={b.id} onClick={() => onChange(active ? null : b.id)} data-testid={`cm-bw-${b.id}`} className={`flex w-full items-center justify-between gap-2 rounded-xl border p-3 text-left ${active ? "border-primary bg-primary/10" : "border-border hover:bg-accent"}`}>
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-foreground">{b.name}</p>
              <p className="truncate text-xs text-muted-foreground">{b.cycle}{b.repetitions ? ` · ${b.repetitions}×` : b.duration_s ? ` · ${b.duration_s}s` : ""}</p>
            </div>
            <span className={`h-4 w-4 shrink-0 rounded-full border-2 ${active ? "border-primary bg-primary" : "border-muted-foreground"}`} />
          </button>
        );
      })}
    </div>
  );
}

function QuestionsEditor({ questions, onChange }) {
  const t = useT();
  const upd = (i, patch) => onChange(questions.map((q, j) => (j === i ? { ...q, ...patch } : q)));
  return (
    <div data-testid="cm-questions">
      <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("cm.questions")}</label>
      {questions.length === 0 ? (
        <p className="rounded-xl border border-dashed border-border bg-card p-3 text-sm text-muted-foreground">{t("cm.no_questions")}</p>
      ) : (
        <div className="space-y-2">
          {questions.map((q, i) => (
            <div key={i} className="rounded-xl border border-border bg-card p-3">
              <div className="mb-2 flex items-center justify-between">
                <span className="text-xs font-semibold text-muted-foreground">#{i + 1}</span>
                <div className="flex items-center gap-2">
                  <button onClick={() => upd(i, { required: !q.required })} data-testid={`cm-q-required-${i}`} className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${q.required ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground"}`}>{t("cm.q_required")}</button>
                  <button onClick={() => onChange(questions.filter((_, j) => j !== i))} data-testid={`cm-q-delete-${i}`}><Trash2 className="h-4 w-4 text-rose-500" /></button>
                </div>
              </div>
              <Textarea className="min-h-[52px]" value={q.prompt || ""} onChange={(e) => upd(i, { prompt: e.target.value })} maxLength={500} placeholder={t("cm.q_prompt_ph")} data-testid={`cm-q-prompt-${i}`} />
            </div>
          ))}
        </div>
      )}
      <Button type="button" variant="outline" className="mt-2 w-full gap-1.5 rounded-full border-dashed" onClick={() => onChange([...questions, { prompt: "", required: false }])} data-testid="cm-q-add"><Plus className="h-4 w-4" /> {t("cm.q_add")}</Button>
    </div>
  );
}

function StepsEditor({ steps, onChange, library }) {
  const t = useT();
  const upd = (i, patch) => onChange(steps.map((s, j) => (j === i ? { ...s, ...patch } : s)));
  const move = (i, to) => {
    if (to < 0 || to >= steps.length) return;
    const clone = [...steps];
    const [it] = clone.splice(i, 1);
    clone.splice(to, 0, it);
    onChange(clone);
  };
  const addStep = (k) => onChange([...steps, { kind: k, title: t(`cm.step_${k}`), ...(STEP_DEFAULTS[k] || {}) }]);

  return (
    <div data-testid="cm-steps">
      <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("cm.steps")}</label>
      {steps.length === 0 ? (
        <p className="rounded-xl border border-dashed border-border bg-card p-3 text-sm text-muted-foreground">{t("cm.steps_empty")}</p>
      ) : (
        <div className="space-y-2">
          {steps.map((st, i) => {
            const Icon = STEP_ICON[st.kind] || STEP_ICON.text;
            const showBody = ["text", "body_scan", "gratitude", "visualization", "movement"].includes(st.kind);
            const showDuration = ["timer", "audio", "video", "breathwork", "body_scan", "movement"].includes(st.kind);
            return (
              <div key={i} className="rounded-xl border border-border bg-card p-3" data-testid={`cm-step-${i}`}>
                <div className="flex items-start gap-2">
                  <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">{i + 1}</div>
                  <div className="flex-1">
                    <div className="flex flex-wrap gap-1">
                      {STEP_KINDS.map((k) => (
                        <button key={k} onClick={() => upd(i, { kind: k })} data-testid={`cm-step-${i}-kind-${k}`} className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${st.kind === k ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground"}`}>{t(`cm.step_${k}`)}</button>
                      ))}
                    </div>
                  </div>
                  <div className="flex shrink-0 flex-col gap-1">
                    <button onClick={() => move(i, i - 1)} disabled={i === 0} className="disabled:opacity-30" data-testid={`cm-step-up-${i}`}><ChevronUp className="h-4 w-4 text-muted-foreground" /></button>
                    <button onClick={() => move(i, i + 1)} disabled={i === steps.length - 1} className="disabled:opacity-30" data-testid={`cm-step-down-${i}`}><ChevronDown className="h-4 w-4 text-muted-foreground" /></button>
                    <button onClick={() => onChange(steps.filter((_, j) => j !== i))} data-testid={`cm-step-del-${i}`}><Trash2 className="h-4 w-4 text-rose-500" /></button>
                  </div>
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <Icon className="h-4 w-4 shrink-0 text-primary" />
                  <Input value={st.title} onChange={(e) => upd(i, { title: e.target.value })} maxLength={140} placeholder={t("cm.step_title_ph")} data-testid={`cm-step-title-${i}`} />
                </div>
                {showBody && <Textarea className="mt-2 min-h-[52px]" value={st.body || ""} onChange={(e) => upd(i, { body: e.target.value })} maxLength={2000} placeholder={t("cm.step_body_ph")} data-testid={`cm-step-body-${i}`} />}
                {showDuration && <Input className="mt-2" type="number" min="0" value={st.duration_s || ""} onChange={(e) => upd(i, { duration_s: parseInt(e.target.value) || 0 })} placeholder={t("cm.step_duration_ph")} data-testid={`cm-step-duration-${i}`} />}
                {st.kind === "audio" && <div className="mt-2"><MediaField kind="audio" value={st.audio_url || ""} onChange={(v) => upd(i, { audio_url: v })} testID={`cm-step-audio-${i}`} /></div>}
                {st.kind === "video" && <div className="mt-2"><MediaField kind="video" value={st.video_url || ""} onChange={(v) => upd(i, { video_url: v })} testID={`cm-step-video-${i}`} /></div>}
                {st.kind === "breathwork" && library.length > 0 && (
                  <div className="mt-2"><TechniquePicker library={library} value={st.breathwork_technique_id} onChange={(id) => upd(i, { breathwork_technique_id: id })} /></div>
                )}
              </div>
            );
          })}
        </div>
      )}
      <div className="mt-2 flex flex-wrap gap-1.5">
        {["breathwork", "body_scan", "gratitude", "audio", "text", "timer"].map((k) => (
          <Button key={k} type="button" size="sm" variant="outline" className="gap-1 rounded-full border-dashed" onClick={() => addStep(k)} data-testid={`cm-step-add-${k}`}>
            <Plus className="h-3.5 w-3.5" /> {t(`cm.step_${k}`)}
          </Button>
        ))}
      </div>
    </div>
  );
}
